/* Abuse report dialog. Designed at 360px first: one column, every control on a
   full touch target, full-screen on phones.

   Un foglio solo per DUE modali — la segnalazione dalla chat in partita
   (online/ChatReportModal.tsx) e quella dal profilo
   (profile/PlayerReportModal.tsx) — perche' il vestito e' lo stesso e due copie
   divergerebbero al primo ritocco. Lo importa il guscio comune
   (common/ReportModal.tsx), che e' l'unico che rende queste classi.
   Il namespace era `chat-report-*` quando la chat era l'unica provenienza; il
   `.chat-report-trigger` che vive in ChatPanel.css NON e' di questo foglio ed e'
   restato col suo nome: e' il bottone che apre, non il dialogo.

   The backdrop has its OWN class instead of the shared `.modal-overlay`: that
   rule lives in SendChallengeModal.css / PlayModals.css / DashboardPage.css,
   which only load with their own lazy chunks — none of which the game screen
   pulls in. Borrowing the class would ship an unstyled backdrop mid-game. */

.report-modal-overlay {
  position: fixed;
  inset: 0;
  /* Il fondo e' privato (vedi sopra), la sua RICETTA no: questo velo era
     `rgba(20,15,8,.55)` + `blur(3px)`, un caldo e una misura che tre fogli si
     erano copiati a vicenda. Dai token e' lo stesso velo di tutti gli altri. */
  background: var(--overlay-veil);
  -webkit-backdrop-filter: blur(var(--overlay-veil-blur));
  backdrop-filter: blur(var(--overlay-veil-blur));
  display: flex;
  justify-content: center;
  align-items: center;
  /* SOPRA il pannello a tutto schermo, e non per modo di dire: questa modale si
     apre da DENTRO un pannello — la chat in partita su mobile, e il profilo di
     un altro giocatore, che e' sempre un pannello. A `--z-modal-partita` (1300)
     finiva sotto `.fs-panel-overlay` (1600), cioe' invisibile proprio sul
     telefono, che e' dove serve di piu'. Vedi il commento del token in
     index.css: c'e' anche la data in cui l'ordine si e' rotto. */
  z-index: var(--z-modal-su-foglio);
  padding: var(--spacing-md);
}

.report-modal-box {
  background:
    radial-gradient(ellipse at 0% 0%, rgba(201, 162, 39, 0.06), transparent 55%),
    var(--glass-bg);
  border: 1px solid var(--color-hairline);
  border-radius: 16px;
  /* Gradino VETRINA: un foglio e' una superficie di primo livello, come una hero.
     I modali dell'app stavano fra i 20 e i 36 — sette valori diversi per la
     stessa cosa — e convergere e' il punto della scala. Sotto i 767 quelli a
     tutto schermo restano a `padding: 0` e delegano a testata e corpo, che e'
     dottrina gia' scritta: li' il fianco e' un gutter di pagina. */
  padding: var(--bordo-vetrina);
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow:
    0 12px 32px var(--color-shadow-strong),
    0 32px 64px var(--color-shadow),
    inset 0 1px 0 var(--color-luce-alta);
}

.report-modal-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.report-modal-head h2 {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  /* The opponent's name is arbitrary: never let it widen the card. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-modal-close {
  min-width: var(--touch-target-min);
  min-height: var(--touch-target-min);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: none;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.report-modal-close:hover {
  background: var(--color-veil);
}

.report-modal-intro {
  margin: 10px 0 14px;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--color-text-secondary);
}

.report-modal-quote {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  margin-bottom: 14px;
  border-left: 3px solid var(--color-error, #E74C3C);
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  background: var(--color-error-veil-weak);
}

.report-modal-quote-author {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.report-modal-quote-text {
  font-size: 0.9rem;
  color: var(--color-text);
  /* Message text is arbitrary user input: one line, ellipsised, never widening. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-modal-reasons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.report-modal-reason {
  min-height: var(--touch-target-min);
  display: flex;
  align-items: center;
  padding: 8px 14px;
  text-align: left;
  font-size: 0.9rem;
  color: var(--color-text);
  background: var(--color-frost);
  border: 1px solid var(--color-hairline-strong);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.report-modal-reason.selected {
  border-color: var(--color-primary);
  background: var(--color-gold-veil);
  font-weight: 600;
}

.report-modal-reason:disabled {
  opacity: 0.6;
  cursor: default;
}

.report-modal-note-label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.report-modal-note {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-hairline-strong);
  border-radius: var(--radius-md);
  background: var(--color-frost-strong);
  color: var(--color-text);
  /* 16px keeps iOS from zooming the viewport when the field takes focus —
     same reason as .chat-input. */
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
}

.report-modal-note-count {
  margin-top: 4px;
  text-align: right;
  font-size: 0.7rem;
  color: var(--color-text-secondary);
}

.report-modal-hint {
  margin: 12px 0 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--color-text-secondary);
}

.report-modal-error {
  margin: 12px 0 0;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  background: var(--color-error-veil);
  color: var(--color-error, #E74C3C);
  font-size: 0.85rem;
}

.report-modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.report-modal-cancel,
.report-modal-submit {
  flex: 1;
  min-height: var(--touch-target-min);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.report-modal-cancel {
  background: var(--color-frost);
  border: 1px solid var(--color-hairline-strong);
  color: var(--color-text);
}

.report-modal-submit {
  background: var(--color-error, #E74C3C);
  border: 1px solid transparent;
  color: var(--color-on-accent);
}

.report-modal-submit:disabled {
  opacity: 0.55;
  cursor: default;
}

.report-modal-done {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 4px 6px;
  color: var(--color-success, #27AE60);
}

.report-modal-done p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-text);
}

@media (hover: none) and (pointer: coarse) {
  .report-modal-reason:active,
  .report-modal-submit:active,
  .report-modal-cancel:active {
    filter: brightness(1.08);
  }
}

/* Phones and small tablets: the dialog owns the screen. Same treatment as the
   in-game settings modal, safe areas included. */
@media (max-width: 899px) {
  .report-modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .report-modal-box {
    max-width: none;
    max-height: none;
    height: 100svh;
    border: none;
    border-radius: 0;
    overflow-y: auto;
    padding-top: calc(20px + env(safe-area-inset-top));
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
    padding-left: calc(20px + env(safe-area-inset-left));
    padding-right: calc(20px + env(safe-area-inset-right));
  }
}

/* Narrow phones: side-by-side actions do not fit once the labels are translated. */
@media (max-width: 480px) {
  .report-modal-actions {
    flex-direction: column-reverse;
  }
}
